import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Card, Icon, PageEyebrow } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { ViewerPhotosSection } from './ViewerPhotosSection.js';

interface DecryptedNode {
  album: dto.Album;
  name: string;
}

interface LoadedTree {
  rootId: string;
  current: dto.Album;
  albumKey: CryptoKey;
  byId: Map<string, DecryptedNode>;
  childrenByParent: Map<string, DecryptedNode[]>;
}

// Viewer-mode album page. The user has a Share record on rootId; the page
// resolves K_album from that share, walks the tree, and renders photos for
// the currently selected node. Read-only — no upload, rename, move, delete.
export function SharedAlbumPage() {
  const { rootId, albumId: routeAlbumId } = useParams<{ rootId: string; albumId?: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [state, setState] = useState<LoadedTree | 'loading' | { error: string }>('loading');

  const currentAlbumId = routeAlbumId ?? rootId;

  useEffect(() => {
    if (!rootId || !currentAlbumId) return;
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        // Resolve K_album from the matching incoming share. We re-list rather
        // than relying on the in-memory cache so a deep link works after a
        // refresh (the cache is module-level and lost on reload).
        const { shares } = await metadata.listIncomingShares(session.jwt);
        const share = shares.find((s) => s.rootAlbumId === rootId);
        if (!share) {
          if (!cancelled) setState({ error: 'condivisione non trovata' });
          return;
        }
        const albumKey = await getAlbumKey(rootId, share.wrappedAlbumKey, session.rsaPriv);

        const { nodes } = await metadata.fetchAlbumTree(rootId, session.jwt);
        const decrypted: DecryptedNode[] = await Promise.all(
          nodes.map(async (album) => {
            try {
              const name = await cv.decryptText(album.encryptedName, albumKey);
              return { album, name };
            } catch {
              return { album, name: '(decrittura fallita)' };
            }
          }),
        );

        const byId = new Map<string, DecryptedNode>();
        const childrenByParent = new Map<string, DecryptedNode[]>();
        for (const node of decrypted) {
          byId.set(node.album.id, node);
          const parentKey = node.album.parentId ?? '';
          const arr = childrenByParent.get(parentKey) ?? [];
          arr.push(node);
          childrenByParent.set(parentKey, arr);
        }
        for (const arr of childrenByParent.values()) {
          arr.sort((a, b) => a.album.createdAt.localeCompare(b.album.createdAt));
        }

        const current = byId.get(currentAlbumId)?.album;
        if (!current) {
          if (!cancelled) setState({ error: 'album non trovato nel sotto-albero' });
          return;
        }
        if (!cancelled) {
          setState({ rootId, current, albumKey, byId, childrenByParent });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ error: err instanceof Error ? err.message : 'errore' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rootId, currentAlbumId, session.jwt, session.rsaPriv, metadata]);

  const breadcrumb = useMemo(() => {
    if (typeof state !== 'object' || 'error' in state) return [] as DecryptedNode[];
    const out: DecryptedNode[] = [];
    let cursor: dto.Album | undefined = state.current;
    while (cursor) {
      const node = state.byId.get(cursor.id);
      if (!node) break;
      out.unshift(node);
      if (cursor.parentId === null) break;
      cursor = state.byId.get(cursor.parentId)?.album;
    }
    return out;
  }, [state]);

  if (!rootId) return null;
  if (state === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in state) return <p className="py-6 text-sm text-error">Errore: {state.error}</p>;

  const children = state.childrenByParent.get(state.current.id) ?? [];

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>viewer · solo lettura</PageEyebrow>
        <Breadcrumb rootId={rootId} nodes={breadcrumb} />
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="font-display text-lg font-semibold">Sotto-album</h3>
        {children.length === 0 ? (
          <Card className="flex flex-col items-center gap-2 py-8 text-center">
            <Icon name="folder_open" className="text-3xl text-on-surface-variant" />
            <p className="text-sm text-on-surface-variant">Nessun sotto-album.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {children.map((child) => (
              <Link
                key={child.album.id}
                to={`/shared/${encodeURIComponent(rootId)}/${encodeURIComponent(child.album.id)}`}
                className="block"
              >
                <Card className="flex flex-col gap-3 transition-colors hover:border-cyan/60">
                  <div className="flex items-center gap-3">
                    <Icon name="folder" className="text-2xl text-cyan" />
                    <h4 className="flex-1 truncate font-display text-base font-semibold">
                      {child.name}
                    </h4>
                    <Icon name="arrow_forward" className="text-base text-on-surface-variant" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <ViewerPhotosSection albumId={state.current.id} rootId={rootId} albumKey={state.albumKey} />

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Icon name="sell" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">Tag</h3>
        </div>
        <p className="text-xs text-on-surface-variant">
          Tag cifrati condivisi dal proprietario della radice. In sola lettura.
        </p>
        <Link to={`/shared/${encodeURIComponent(rootId)}/tags`} className="self-start">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan transition-colors hover:underline">
            Sfoglia i tag
            <Icon name="arrow_forward" className="text-base" />
          </span>
        </Link>
      </Card>

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Icon name="star" filled className="text-2xl text-warning" />
          <h3 className="font-display text-lg font-semibold">Più valutate</h3>
        </div>
        <p className="text-xs text-on-surface-variant">
          Solo le foto valutate dal proprietario, ordinate per stelle decifrate sul tuo dispositivo.
        </p>
        <Link to={`/shared/${encodeURIComponent(rootId)}/top-rated`} className="self-start">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan transition-colors hover:underline">
            Apri classifica
            <Icon name="arrow_forward" className="text-base" />
          </span>
        </Link>
      </Card>
    </div>
  );
}

function Breadcrumb({ rootId, nodes }: { rootId: string; nodes: DecryptedNode[] }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to="/shared" className="text-on-surface-variant transition-colors hover:text-cyan">
        Condivisi
      </Link>
      {nodes.map((node, idx) => (
        <span key={node.album.id} className="flex items-center gap-2">
          <Icon name="chevron_right" className="text-base text-on-surface-variant" />
          {idx === nodes.length - 1 ? (
            <span className="font-display text-base font-semibold text-cyan">{node.name}</span>
          ) : (
            <Link
              to={
                idx === 0
                  ? `/shared/${encodeURIComponent(rootId)}`
                  : `/shared/${encodeURIComponent(rootId)}/${encodeURIComponent(node.album.id)}`
              }
              className="text-on-surface-variant transition-colors hover:text-cyan"
            >
              {node.name}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}
